/**
 * 内容识别缩放的应用层接线（M4-01 / DESIGN.md §6.5）。
 *
 * MVP 范围：对整份文档的合成结果做 Seam Carving，结果写回活动 raster
 * 图层并把画布改成目标尺寸——多图层各自独立缩放是更复杂的产品决策
 * （每层内容语义不同，缩放策略也该不同），留到有真实需求时再做。
 * 当前选区（若有）作为保护区：用户选中的通常是想保留的主体。
 */
import { compositeDocument } from './composite';
import { createSetCanvasSizeCommand } from './commands/canvas-commands';
import { seamCarve } from '../engine/seam-carve';
import { TILE, tileKey } from '../engine/tile/key';
import { BYTES_PER_TILE } from '../engine/tile/store';
import type { AppStore } from './store';

export interface SeamCarveCallOptions {
  readonly signal?: AbortSignal;
}

/** 把当前选区光栅化成与文档同尺寸的单通道保护蒙版（选中处保护，值 255）。 */
function selectionAsProtectMask(store: AppStore, width: number, height: number): Uint8Array | undefined {
  const selection = store.selection;
  if (selection === null || selection.isEmpty) return undefined;
  const mask = new Uint8Array(width * height);
  const row = new Uint8Array(width);
  for (let y = 0; y < height; y++) {
    selection.readRow(y, 0, width, row);
    mask.set(row, y * width);
  }
  return mask;
}

/**
 * 对文档做内容识别缩放到目标尺寸，写回活动 raster 图层，画布尺寸随之改变。
 * 一次调用 = 一条历史（画布尺寸 + 像素）。返回是否执行。
 */
export async function seamCarveDocument(
  store: AppStore,
  targetWidth: number,
  targetHeight: number,
  options: SeamCarveCallOptions = {},
): Promise<boolean> {
  const document = store.document;
  if (document === null) return false;
  if (targetWidth < 1 || targetHeight < 1) return false;

  const target = store.activeRasterLayerId();
  if (target === null) return false;

  const flat = compositeDocument(document, store.tiles);
  const protectMask = selectionAsProtectMask(store, flat.width, flat.height);

  const result = seamCarve(
    flat.pixels,
    flat.width,
    flat.height,
    Math.round(targetWidth),
    Math.round(targetHeight),
    protectMask === undefined ? {} : { protect: protectMask },
    options,
  );

  const transaction = store.beginPixelTransaction('内容识别缩放');
  const tx1 = Math.ceil(result.width / TILE) - 1;
  const ty1 = Math.ceil(result.height / TILE) - 1;
  const keys = [];
  for (let ty = 0; ty <= ty1; ty++) {
    for (let tx = 0; tx <= tx1; tx++) {
      const key = tileKey(target, tx, ty);
      transaction.touch(key);
      keys.push({ tx, ty, key });
    }
  }
  transaction.capture();

  const dirty: Array<{ tx: number; ty: number }> = [];
  for (const { tx, ty, key } of keys) {
    const out = new Uint8Array(BYTES_PER_TILE);
    const originX = tx * TILE;
    const originY = ty * TILE;
    let any = false;
    for (let y = 0; y < TILE; y++) {
      const docY = originY + y;
      if (docY >= result.height) break;
      for (let x = 0; x < TILE; x++) {
        const docX = originX + x;
        if (docX >= result.width) break;
        const from = (docY * result.width + docX) * 4;
        if (result.pixels[from + 3] === 0) continue;
        const to = (y * TILE + x) * 4;
        out[to] = result.pixels[from]!;
        out[to + 1] = result.pixels[from + 1]!;
        out[to + 2] = result.pixels[from + 2]!;
        out[to + 3] = result.pixels[from + 3]!;
        any = true;
      }
    }
    if (any) {
      store.tiles.set(key, out);
      dirty.push({ tx, ty });
    }
  }

  store.setSelection(null); // 选区坐标随缩放失义
  store.dispatchWithPixels(
    createSetCanvasSizeCommand(result.width, result.height),
    transaction.end(),
  );
  store.invalidatePixels(dirty);
  return true;
}
